import {
  ArrowRightIcon,
  Badge,
  Box,
  Button,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  QuickFilter,
  QuickFilterGroup,
  RefreshIcon,
  SearchInput,
  Text,
} from '@razorpay/blade/components';
import { useSalaryComponentHelpers } from 'components/SalaryComponents/hooks';
import { SalaryComponent } from 'components/SalaryComponents/types';
import React, { useEffect, useState } from 'react';
import { AddComponentModalProps } from '../types';
import ComponentsTable from './ComponentsTable';

const AddComponentModal: React.FC<AddComponentModalProps> = ({
  isOpen,
  onClose,
  availableComponents,
  selectedComponentIds,
  onAddComponents,
  refetchComponents,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedComponentIds);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { openEarningsCreationInNewTab } = useSalaryComponentHelpers();

  useEffect(() => {
    if (!isOpen) {
      setSelectedIds(selectedComponentIds);
    }
  }, [isOpen, selectedComponentIds]);

  const getFilteredComponents = (): SalaryComponent[] => {
    let filtered = [...availableComponents];

    if (activeTab === 'earning') {
      filtered = filtered.filter(
        (component) =>
          component.category === 'EARNINGS_CATEGORY' &&
          (component.settings as any)?.earningDetails?.payType !== 'non_payable_benefit',
      );
    } else if (activeTab === 'non-payable') {
      filtered = filtered.filter(
        (component) =>
          component.category === 'EARNINGS_CATEGORY' &&
          (component.settings as any)?.earningDetails?.payType === 'non_payable_benefit',
      );
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (component) =>
          component.name.toLowerCase().includes(query) ||
          (component.description && component.description.toLowerCase().includes(query)),
      );
    }

    return filtered;
  };

  const filteredComponents = getFilteredComponents();

  // Handle add to template
  const handleAddToTemplate = () => {
    const selectedComponents = availableComponents.filter((component) =>
      selectedIds.includes(component.id),
    );
    onAddComponents(selectedComponents);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onDismiss={onClose} size="large">
      <ModalHeader
        title="Add component to template"
        trailing={
          <Badge color="positive">{`${selectedComponentIds.length} components added`}</Badge>
        }
      />
      <ModalBody>
        <Box display="flex" justifyContent="space-between">
          <QuickFilterGroup
            onChange={(e) => setActiveTab(e.values[0])}
            defaultValue="all"
            selectionType="single"
            value={activeTab}>
            <Box display="flex" gap="spacing.4" alignItems="center">
              <QuickFilter key="all" value="all" title="All" />
              <QuickFilter key="earning" value="earning" title="Earnings" />
              <QuickFilter key="non-payable" value="non-payable" title="Non-Payable Benefits" />
            </Box>
          </QuickFilterGroup>
          <Box display="flex" alignItems="center" gap="spacing.4">
            <Button icon={RefreshIcon} onClick={refetchComponents} variant="tertiary" />
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.value || '')}
              placeholder="Search component"
              accessibilityLabel="Search component"
            />
          </Box>
        </Box>

        <Box marginTop="spacing.7">
          <ComponentsTable
            components={filteredComponents}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        </Box>

        {filteredComponents.length === 0 && (
          <Box textAlign="center" paddingY="spacing.8">
            <Text color="surface.text.gray.muted">Didn't find the component?</Text>
            <Box marginTop="spacing.3">
              <Link
                icon={ArrowRightIcon}
                iconPosition="right"
                onClick={openEarningsCreationInNewTab}>
                Create component
              </Link>
            </Box>
          </Box>
        )}
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" flexDirection="column">
            <Text>Didn't find the component?</Text>
            <Link icon={ArrowRightIcon} iconPosition="right" onClick={openEarningsCreationInNewTab}>
              Create component
            </Link>
          </Box>

          <Box display="flex" gap="spacing.5">
            <Button variant="tertiary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddToTemplate}
              isDisabled={selectedIds.length === 0}>
              Add to template ({selectedIds.length})
            </Button>
          </Box>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export default AddComponentModal;
